import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useSlabData } from '../hooks/useSlabData';
import './CompareModal.css';

// ── Constants mirrored from MobileBottomSheet ────────────────────────────────
const BRAND_DATA = {
  'Dekton':         { finishes: ['Polished', 'Velvet', 'Matte'], thicknesses: ['4mm', '8mm', '12mm', '20mm'], applications: ['Countertops', 'Vanities', 'Facades', 'Flooring'] },
  'Caesarstone':    { finishes: ['Polished', 'Honed', 'Natural'], thicknesses: ['13mm', '20mm', '30mm'],       applications: ['Countertops', 'Vanities', 'Wall Cladding'] },
  'Granite/Marble': { finishes: ['Polished', 'Leathered', 'Honed'], thicknesses: ['20mm', '30mm'],             applications: ['Countertops', 'Vanities', 'Flooring'] },
  'Porcelain':      { finishes: ['Polished', 'Silk', 'Matte'],   thicknesses: ['6mm', '12mm', '20mm'],         applications: ['Countertops', 'Vanities', 'Wall Cladding', 'Flooring'] },
};

const MAINT_LABELS = { none: 'None', low: 'Low', moderate: 'Moderate' };
const MAINT_COLORS = { none: '#4caf50', low: '#8bc34a', moderate: '#ff9800' };
const PRICE_LABELS  = { entry: 'Entry', mid: 'Mid', premium: 'Premium', luxury: 'Luxury' };

const DurabilityDots = ({ score, max = 5 }) => (
  <span className="cm-durability-dots">
    {Array.from({ length: max }, (_, i) => (
      <span key={i} className={`cm-dot${i < score ? ' filled' : ''}`} />
    ))}
  </span>
);

// ── ItemDetail — specs + properties for one item ─────────────────────────────
const ItemDetail = ({ item, slabData }) => {
  const brand     = BRAND_DATA[item.brandName] || BRAND_DATA['Dekton'];
  const finishes  = slabData?.finishes     || brand.finishes;
  const thicknesses = slabData?.thicknesses || brand.thicknesses;
  const applications = slabData?.applications || brand.applications;
  const collection = slabData?.collection || null;
  const desc = slabData?.description || null;

  const subtitle = [
    collection,
    finishes[0],
    thicknesses.length ? thicknesses.join(' / ') : null,
  ].filter(Boolean).join(' · ');

  return (
    <div className="cm-details">
      <p className="cm-detail-name">{slabData?.name || item.productName}</p>
      {subtitle && <p className="cm-detail-subtitle">{subtitle}</p>}
      {desc && <p className="cm-detail-desc">{desc}</p>}

      <div className="cm-spec-row">
        <span className="cm-spec-label">Finishes</span>
        <div className="cm-tags">
          {finishes.slice(0, 4).map((f, i) => <span key={i} className="cm-tag">{f}</span>)}
        </div>
      </div>
      <div className="cm-spec-row">
        <span className="cm-spec-label">Thicknesses</span>
        <span className="cm-spec-value">{thicknesses.join(' · ')}</span>
      </div>
      <div className="cm-spec-row">
        <span className="cm-spec-label">Applications</span>
        <div className="cm-tags">
          {applications.slice(0, 4).map((a, i) => <span key={i} className="cm-tag cm-tag--app">{a}</span>)}
        </div>
      </div>

      {slabData && (
        <div className="cm-props">
          {slabData.naturalStone !== undefined && (
            <div className="cm-prop">
              <span className="cm-prop-value">{slabData.naturalStone ? '✓' : '✗'}</span>
              <span className="cm-prop-label">Natural</span>
            </div>
          )}
          {slabData.outdoorRated !== undefined && (
            <div className="cm-prop">
              <span className="cm-prop-value">{slabData.outdoorRated ? '✓' : '✗'}</span>
              <span className="cm-prop-label">Outdoor</span>
            </div>
          )}
          {slabData.durabilityScore && (
            <div className="cm-prop">
              <DurabilityDots score={slabData.durabilityScore} />
              <span className="cm-prop-label">Durability</span>
            </div>
          )}
          {slabData.maintenanceLevel && (
            <div className="cm-prop">
              <span
                className="cm-prop-value"
                style={{ color: MAINT_COLORS[slabData.maintenanceLevel] || '#555', fontSize: '11px' }}
              >
                {MAINT_LABELS[slabData.maintenanceLevel] || slabData.maintenanceLevel}
              </span>
              <span className="cm-prop-label">Care</span>
            </div>
          )}
          {slabData.priceTier && (
            <div className="cm-prop">
              <span className="cm-prop-value" style={{ fontSize: '11px' }}>
                {PRICE_LABELS[slabData.priceTier] || slabData.priceTier}
              </span>
              <span className="cm-prop-label">Tier</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Webhook tracker — fires once per unique pair ─────────────────────────────
const fireCompareWebhook = (compareItems) => {
  if (compareItems.length < 2) return;
  fetch('https://elegantfittings.app.n8n.cloud/webhook/color', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      sessionId: `session_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`,
      timestamp: new Date().toISOString(),
      colors: compareItems.map(item => ({
        id: item.id,
        productName: item.productName,
        brandName: item.brandName || 'DEKTON',
        productImage: item.productImage,
      })),
    }),
  }).catch(err => console.error('Compare webhook error:', err));
};

// ── Main component ────────────────────────────────────────────────────────────
const CompareModal = ({
  isOpen,
  onClose,
  compareItems = [],
  onRemove,
  onClearAll: _onClearAll,
  onToggleLike: _onToggleLike,
  isVisualLiked: _isVisualLiked,
  onAddToCart: _onAddToCart,
}) => {
  const { findSlab } = useSlabData();

  // Slider state — ALL hooks must be unconditional
  const [sliderPct, setSliderPct] = useState(50);
  const sliderRef = useRef(null);
  const dragging = useRef(false);

  // Derive display order
  const hasTwo = compareItems.length === 2;
  const leftItem  = hasTwo ? compareItems[0] : compareItems[0] || null;
  const rightItem = hasTwo ? compareItems[1] : null;

  // Resolve slab data
  const leftSlab  = leftItem  ? findSlab(leftItem.productImage)  : null;
  const rightSlab = rightItem ? findSlab(rightItem.productImage) : null;

  const updateSlider = useCallback((clientX) => {
    const el = sliderRef.current;
    if (!el) return;
    const { left, width } = el.getBoundingClientRect();
    const pct = Math.min(100, Math.max(0, ((clientX - left) / width) * 100));
    setSliderPct(pct);
  }, []);

  useEffect(() => {
    const onMove = (e) => {
      if (!dragging.current) return;
      updateSlider(e.touches ? e.touches[0].clientX : e.clientX);
    };
    const onUp = () => { dragging.current = false; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: true });
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
  }, [updateSlider]);

  if (!isOpen) return null;

  const startDrag = (e) => {
    dragging.current = true;
    updateSlider(e.touches ? e.touches[0].clientX : e.clientX);
  };

  return (
    <div className="cm-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="cm-panel">
        {/* Sticky header */}
        <div className="cm-header">
          <div className="cm-drag-handle" />
          <span className="cm-title">
            {hasTwo ? 'COMPARING 2 SLABS' : compareItems.length === 1 ? 'COMPARING — ADD 1 MORE' : 'COMPARE SLABS'}
          </span>
          <button className="cm-close-btn" onClick={onClose} aria-label="Close">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M3 3L13 13M13 3L3 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {/* ── Before/after split slider ── */}
        {hasTwo ? (
          <div
            className="cm-slider"
            ref={sliderRef}
            onMouseDown={startDrag}
            onTouchStart={startDrag}
          >
            {/* Right image — full width, underneath */}
            <div className="cm-slider-right">
              <img src={rightItem.productImage || rightItem.image} alt={rightItem.productName} />
              <span className="cm-img-label cm-img-label--right">{rightItem.productName}</span>
              <button className="cm-img-remove" onClick={(e) => { e.stopPropagation(); onRemove && onRemove(rightItem.id); }}>×</button>
            </div>

            {/* Left image — clipped to sliderPct */}
            <div className="cm-slider-left" style={{ width: `${sliderPct}%` }}>
              <img src={leftItem.productImage || leftItem.image} alt={leftItem.productName} style={{ width: sliderRef.current?.offsetWidth ? `${sliderRef.current.offsetWidth}px` : '100vw' }} />
              <span className="cm-img-label">{leftItem.productName}</span>
              <button className="cm-img-remove" onClick={(e) => { e.stopPropagation(); onRemove && onRemove(leftItem.id); }}>×</button>
            </div>

            {/* Divider line + handle */}
            <div className="cm-slider-divider" style={{ left: `${sliderPct}%` }}>
              <div className="cm-slider-handle">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <path d="M8 6L3 12L8 18M16 6L21 12L16 18"/>
                </svg>
              </div>
            </div>
          </div>
        ) : (
          /* Single item or empty */
          <div className="cm-images">
            {leftItem && (
              <div className="cm-image-slot">
                <img src={leftItem.productImage || leftItem.image} alt={leftItem.productName} />
                <span className="cm-img-label">{leftItem.productName}</span>
                <button className="cm-img-remove" onClick={() => onRemove && onRemove(leftItem.id)}>×</button>
              </div>
            )}
            <div className="cm-image-slot cm-image-slot--empty"><span>+</span><span>Add slab</span></div>
          </div>
        )}

        {/* Detail section — side-by-side on desktop, stacked on mobile */}
        <div className="cm-details-row">
          {leftItem ? (
            <ItemDetail item={leftItem} slabData={leftSlab} />
          ) : (
            <p className="cm-empty-msg">Add slabs using the Compare button on any material.</p>
          )}
          {hasTwo && rightItem && (
            <ItemDetail item={rightItem} slabData={rightSlab} />
          )}
        </div>
      </div>
    </div>
  );
};

export default CompareModal;
